import type { JSONContent } from '@tiptap/core'
import { Markdown, MarkdownManager } from '@tiptap/markdown'
import JSZip from 'jszip'

import { getExtensions } from '../components/RichText/helper'

/**
 * 单个待导入的 markdown 文件条目。
 * - `name`：文件名（不含 `.md` 后缀），用作映射字段匹配键。
 * - `fileName`：原始文件名（含后缀），用于展示。
 * - `content`：markdown 文本。
 */
export interface MdFileEntry {
    name: string
    fileName: string
    content: string
    size: number
}

const MARKDOWN_IMPORT_SIZE_LIMIT = 100 * 1024 * 1024

let markdownManager: MarkdownManager | null = null

/**
 * 获取共享的 MarkdownManager 实例。
 * 复用与笔记字段一致的扩展集（`getExtensions({ mode: 'all' })`），
 * 并附带 `VariableBlock`（用于序列化变量节点）与官方 `Markdown` 扩展。
 */
export const getMarkdownManager = (): MarkdownManager => {
    if (!markdownManager) {
        markdownManager = new MarkdownManager({
            extensions: [...getExtensions({ mode: 'all', variable: {} }), Markdown]
        })
    }
    return markdownManager
}

/**
 * 将 markdown 字符串解析为 Tiptap JSON（与笔记字段存储格式一致）。
 */
export const markdownToTiptapJson = (md: string): JSONContent => {
    return getMarkdownManager().parse(md)
}

/**
 * 将 Tiptap JSON 序列化为 markdown 字符串。
 */
export const tiptapJsonToMarkdown = (json: JSONContent): string => {
    return getMarkdownManager().serialize(json)
}

const stripMdExtension = (fileName: string): string => {
    return fileName.replace(/\.md$/i, '')
}

const readFileAsText = (file: File | Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result ?? ''))
        reader.onerror = () => reject(reader.error ?? new Error('读取文件失败'))
        reader.readAsText(file)
    })

/**
 * 校验文件大小不超过 100MB。
 */
export const assertMarkdownFileSize = (size: number): void => {
    if (size > MARKDOWN_IMPORT_SIZE_LIMIT) {
        throw new Error('文件大小不能超过 100 MB')
    }
}

/**
 * 从上传的 `.md` 文件或 `.zip` 压缩包中提取 markdown 文件条目。
 * - `.md`：直接读取文本。
 * - `.zip`：解压并收集其中所有 `.md` 文件（按设计稿约束，md 应位于单一文件夹内）。
 * - `.rar`：浏览器端无可靠解码库，抛错提示转为 zip。
 */
export const extractMdFiles = async (file: File): Promise<MdFileEntry[]> => {
    assertMarkdownFileSize(file.size)
    const lowerName = file.name.toLowerCase()

    if (lowerName.endsWith('.rar')) {
        throw new Error('暂不支持 rar 格式，请将压缩包转为 zip 后上传')
    }

    if (lowerName.endsWith('.zip')) {
        const zip = await JSZip.loadAsync(file)
        const entries: MdFileEntry[] = []
        const promises: Promise<void>[] = []

        zip.forEach((relativePath, entry) => {
            if (entry.dir) {
                return
            }
            if (!entry.name.toLowerCase().endsWith('.md')) {
                return
            }
            // 过滤 macOS 压缩产生的垃圾：__MACOSX 目录、AppleDouble 元数据（._ 前缀）、隐藏文件
            const segments = relativePath.split('/')
            if (segments.some(segment => segment === '__MACOSX' || segment.startsWith('.') || segment.startsWith('._'))) {
                return
            }
            const fileName = segments.pop() || entry.name
            if (fileName.startsWith('.') || fileName.startsWith('._')) {
                return
            }
            promises.push(
                entry.async('string').then(content => {
                    entries.push({
                        name: stripMdExtension(fileName),
                        fileName,
                        content,
                        size: content.length
                    })
                })
            )
        })

        await Promise.all(promises)
        return entries
    }

    if (lowerName.endsWith('.md')) {
        const content = await readFileAsText(file)
        return [
            {
                name: stripMdExtension(file.name),
                fileName: file.name,
                content,
                size: content.length
            }
        ]
    }

    throw new Error('仅支持 .md、.zip 格式的文件')
}
